import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ArrowRight, Heart, Home, Medal, BookOpen, Users, Baby, MapPin, Briefcase, ShieldCheck, CheckCircle2, Loader2, Upload, ListChecks, Award, Lock, ChevronDown, ChevronLeft, ChevronRight, Flame, Link2, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Eyebrow, SectionTitle, GridBg, GlowOrb } from '../components/SectionHeader';
import { benefits, cultureBullets, cultureStats } from '../mock';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';
import { getPublicJobs, submitApplication } from '../api';

const iconMap = { Heart, Home, Medal, BookOpen, Users, Baby };

const JobSection = ({ title, icon: Icon, html, testId }) => (
  <div data-testid={testId}>
    <p className="text-xs tracking-wider text-cyan-400 uppercase mb-2 inline-flex items-center gap-1"><Icon className="w-3.5 h-3.5" /> {title}</p>
    <div className="text-sm text-slate-300 leading-relaxed rich-content" dangerouslySetInnerHTML={{ __html: html }} />
  </div>
);

const JobCard = ({ job, onApply, initialOpen = false }) => {
  const [open, setOpen] = React.useState(initialOpen);
  const [copied, setCopied] = React.useState(false);
  React.useEffect(() => { if (initialOpen) setOpen(true); }, [initialOpen]);

  const copyLink = async (e) => {
    e.stopPropagation();
    const url = `${window.location.origin}/careers?job=${job.job_number || job.id}`;
    try {
      if (navigator.share && /Mobi|Android/i.test(navigator.userAgent)) {
        await navigator.share({ title: `${job.title} – IronVision`, url });
      } else {
        await navigator.clipboard.writeText(url);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* user cancelled */ }
  };

  return (
    <div className="rounded-2xl border border-cyan-400/15 bg-slate-900/50 hover:border-cyan-400/40 transition">
      <button
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
        className="w-full text-left p-5 md:p-6 flex items-start gap-4"
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 mb-2 flex-wrap">
            <h3 className="text-lg md:text-xl font-semibold text-white">{job.title}</h3>
            {job.is_hot && (
              <span className="inline-flex items-center gap-1 text-[10px] tracking-widest px-2 py-1 rounded bg-orange-500/15 text-orange-300 border border-orange-400/30 uppercase font-semibold">
                <Flame className="w-3 h-3" /> Hot
              </span>
            )}
            <span className="text-[10px] tracking-widest px-2 py-1 rounded bg-cyan-400/10 text-cyan-300 border border-cyan-400/20 uppercase">{job.tag}</span>
            {job.job_number && <span className="text-[11px] font-mono text-slate-500">#{job.job_number}</span>}
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-2 text-xs text-slate-400">
            <span className="inline-flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" /> {job.location}</span>
            <span className="inline-flex items-center gap-1.5"><Briefcase className="w-3.5 h-3.5" /> {job.type}</span>
            {job.clearance_level && job.clearance_level !== 'None' && <span className="inline-flex items-center gap-1.5"><Lock className="w-3.5 h-3.5" /> {job.clearance_level}</span>}
          </div>
        </div>
        <ChevronDown className={`w-5 h-5 text-cyan-400 flex-shrink-0 mt-1 transition-transform duration-300 ${open ? 'rotate-180' : ''}`} />
      </button>
      <div className={`grid transition-all duration-300 ease-in-out ${open ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`}>
        <div className="overflow-hidden">
          <div className="px-5 md:px-6 pb-6 pt-1 border-t border-cyan-400/10">
            <div data-testid={`job-description-${job.id}`} className="mt-4 text-sm text-slate-300 leading-relaxed rich-content" dangerouslySetInnerHTML={{ __html: job.description }} />
            {(job.requirements || job.certifications) && (
              <div className="mt-5 grid sm:grid-cols-2 gap-5">
                {job.requirements && <JobSection title="Requirements" icon={ListChecks} html={job.requirements} testId={`job-requirements-${job.id}`} />}
                {job.certifications && <JobSection title="Certifications" icon={Award} html={job.certifications} testId={`job-certifications-${job.id}`} />}
              </div>
            )}
            {job.benefits && <div className="mt-5"><JobSection title="Benefits" icon={Heart} html={job.benefits} testId={`job-benefits-${job.id}`} /></div>}
            {job.about && <div className="mt-5"><JobSection title="About IronVision" icon={ShieldCheck} html={job.about} testId={`job-about-${job.id}`} /></div>}
            {job.eeo && <div data-testid={`job-eeo-${job.id}`} className="mt-5 text-xs text-slate-500 leading-relaxed rich-content" dangerouslySetInnerHTML={{ __html: job.eeo }} />}
            <div className="mt-6 flex flex-col sm:flex-row gap-3 sm:justify-end">
              <Button onClick={copyLink} variant="outline" className="border-cyan-400/40 bg-transparent hover:bg-cyan-400/10 text-cyan-300 rounded-md h-11 px-5">
                {copied ? (<><Check className="w-4 h-4 mr-1" /> Link copied</>) : (<><Link2 className="w-4 h-4 mr-1" /> Share position</>)}
              </Button>
              <Button onClick={() => onApply(job)} className="bg-cyan-400 hover:bg-cyan-300 text-slate-900 font-semibold rounded-md h-11 px-6">
                Apply Now <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const Careers = ({ onContact }) => {
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const sharedJobParam = searchParams.get('job');
  const [applyOpen, setApplyOpen] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', cover: '', website: '' });
  const [resumeFile, setResumeFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [jobsLoading, setJobsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 10;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await getPublicJobs();
        if (!cancelled) setJobs(data);
      } catch (err) {
        console.warn('Failed to load jobs:', err?.message || err);
      } finally {
        if (!cancelled) setJobsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // If URL has ?job=<number|id>, jump to that job's page and auto-expand it
  useEffect(() => {
    if (!sharedJobParam || jobs.length === 0) return;
    const idx = jobs.findIndex(j => j.job_number === sharedJobParam || j.id === sharedJobParam);
    if (idx === -1) return;
    setPage(Math.floor(idx / PAGE_SIZE) + 1);
    setTimeout(() => {
      const el = document.getElementById(`job-${jobs[idx].id}`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 200);
  }, [sharedJobParam, jobs]);

  const openApply = (job) => { setSelectedJob(job); setApplyOpen(true); };
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('name', form.name);
      fd.append('email', form.email);
      fd.append('cover', form.cover);
      fd.append('website', form.website || ''); // honeypot
      fd.append('job_title', selectedJob?.title || 'General');
      if (selectedJob?.id) fd.append('job_id', selectedJob.id);
      if (resumeFile) fd.append('resume', resumeFile);
      await submitApplication(fd);
      toast({ title: 'Application received', description: "We'll review and reach out soon." });
      setApplyOpen(false);
      setForm({ name: '', email: '', cover: '', website: '' });
      setResumeFile(null);
    } catch (err) {
      toast({ title: 'Submission failed', description: err?.response?.data?.detail || 'Please try again.', variant: 'destructive' });
    } finally { setLoading(false); }
  };

  const onFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) { setResumeFile(null); return; }
    if (f.size > 5 * 1024 * 1024) {
      toast({ title: 'File too large', description: 'Resume must be 5MB or less.', variant: 'destructive' });
      e.target.value = '';
      return;
    }
    setResumeFile(f);
  };

  return (
    <div className="bg-[#0a0e1a]">
      <section className="relative pt-32 pb-20 overflow-hidden">
        <GridBg />
        <GlowOrb className="w-[600px] h-[600px] -top-32 right-0" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-cyan-400/40 bg-cyan-400/5 text-cyan-300 text-xs font-medium">
            <ShieldCheck className="w-3.5 h-3.5" /> Careers
          </span>
          <h1 className="mt-6 text-5xl md:text-7xl font-bold text-white leading-[1.05] tracking-tight max-w-4xl">
            Join Our <span className="text-cyan-400">Mission</span>
          </h1>
          <p className="mt-6 text-lg text-slate-300 max-w-2xl leading-relaxed">
            Help us deliver cybersecurity and compliance capabilities to federal and regulated clients. We are seeking professionals committed to mission impact in cybersecurity and GRC.
          </p>
          <Button asChild className="mt-10 bg-cyan-400 hover:bg-cyan-300 text-slate-900 font-semibold h-12 px-7 rounded-md shadow-[0_0_28px_rgba(34,211,238,0.4)]">
            <a href="#jobs">View Open Positions <ArrowRight className="w-4 h-4 ml-1" /></a>
          </Button>
        </div>
      </section>

      <section className="py-24 bg-[#080c17] border-y border-cyan-400/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-2 gap-16 items-start">
          <div>
            <Eyebrow>Culture</Eyebrow>
            <SectionTitle>How We <span className="text-cyan-400">Work</span></SectionTitle>
            <p className="mt-6 text-slate-300 leading-relaxed">We're a remote-first team that values impact over hours. Our culture is built on trust, transparency, and a shared commitment to our mission.</p>
            <ul className="mt-8 space-y-3">
              {cultureBullets.map(b => (
                <li key={b} className="flex gap-3 text-slate-200"><CheckCircle2 className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" /><span>{b}</span></li>
              ))}
            </ul>
          </div>
          <div className="grid grid-cols-2 gap-5">
            {cultureStats.map(s => (
              <div key={s.value + s.label} className="rounded-2xl border border-cyan-400/15 bg-gradient-to-br from-slate-900/60 to-slate-950/30 p-7 text-center">
                <p className="text-3xl font-bold text-cyan-400">{s.value}</p>
                <p className="mt-2 text-sm text-slate-400 tracking-wide uppercase">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <Eyebrow>Benefits</Eyebrow>
            <SectionTitle>We Take Care of <span className="text-cyan-400">Our Team</span></SectionTitle>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map(b => {
              const Icon = iconMap[b.icon];
              return (
                <div key={b.title} className="rounded-2xl border border-cyan-400/10 bg-slate-900/40 p-7 hover:border-cyan-400/40 hover:-translate-y-1 transition duration-300">
                  <div className="w-12 h-12 rounded-lg bg-cyan-400/10 border border-cyan-400/30 flex items-center justify-center">
                    {Icon && <Icon className="w-6 h-6 text-cyan-400" />}
                  </div>
                  <h3 className="mt-5 text-lg font-semibold text-white">{b.title}</h3>
                  <p className="mt-3 text-sm text-slate-400 leading-relaxed">{b.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section id="jobs" className="py-28 bg-[#080c17] border-y border-cyan-400/10">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <Eyebrow>Open Positions</Eyebrow>
            <SectionTitle>Join Our <span className="text-cyan-400">Team</span></SectionTitle>
            <p className="mt-5 text-slate-400 max-w-2xl mx-auto">We are always looking for talented Engineering and Business professionals.</p>
          </div>
          {jobsLoading ? (
            <div className="text-center text-slate-400 py-10"><Loader2 className="w-5 h-5 animate-spin inline" /> Loading positions...</div>
          ) : jobs.length === 0 ? (
            <div className="text-center text-slate-400 py-10">No open positions right now. Check back soon.</div>
          ) : (
            <div className="space-y-4">
              {jobs.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE).map(job => (
                <div key={job.id} id={`job-${job.id}`}>
                  <JobCard
                    job={job}
                    onApply={openApply}
                    initialOpen={sharedJobParam === job.job_number || sharedJobParam === job.id}
                  />
                </div>
              ))}
            </div>
          )}
          {jobs.length > PAGE_SIZE && (
            <div className="mt-10 flex items-center justify-between gap-4">
              <p className="text-xs text-slate-500">
                Showing <span className="text-slate-300 font-medium">{(page - 1) * PAGE_SIZE + 1}&ndash;{Math.min(page * PAGE_SIZE, jobs.length)}</span> of <span className="text-slate-300 font-medium">{jobs.length}</span>
              </p>
              <div className="flex items-center gap-2">
                <Button
                  onClick={() => { setPage(p => Math.max(1, p - 1)); document.getElementById('jobs')?.scrollIntoView({ behavior: 'smooth' }); }}
                  disabled={page === 1}
                  variant="outline"
                  className="h-9 border-slate-700 bg-transparent hover:bg-slate-800 text-slate-200 disabled:opacity-40"
                >
                  <ChevronLeft className="w-4 h-4 mr-1" /> Previous
                </Button>
                {Array.from({ length: Math.ceil(jobs.length / PAGE_SIZE) }, (_, i) => i + 1).map(n => (
                  <button
                    key={n}
                    onClick={() => { setPage(n); document.getElementById('jobs')?.scrollIntoView({ behavior: 'smooth' }); }}
                    className={`w-9 h-9 rounded-md text-sm font-medium transition ${n === page ? 'bg-cyan-400 text-slate-900' : 'bg-slate-800/60 text-slate-300 hover:bg-slate-800 border border-slate-700'}`}
                    aria-current={n === page ? 'page' : undefined}
                  >
                    {n}
                  </button>
                ))}
                <Button
                  onClick={() => { setPage(p => Math.min(Math.ceil(jobs.length / PAGE_SIZE), p + 1)); document.getElementById('jobs')?.scrollIntoView({ behavior: 'smooth' }); }}
                  disabled={page === Math.ceil(jobs.length / PAGE_SIZE)}
                  variant="outline"
                  className="h-9 border-slate-700 bg-transparent hover:bg-slate-800 text-slate-200 disabled:opacity-40"
                >
                  Next <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </div>
          )}
        </div>
      </section>

      <section className="py-28 relative overflow-hidden">
        <GlowOrb className="w-[500px] h-[500px] top-0 left-1/2 -translate-x-1/2" />
        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white leading-tight">Don't See the <span className="text-cyan-400">Right Role?</span></h2>
          <p className="mt-6 text-slate-300 max-w-2xl mx-auto">We're always interested in meeting talented people. Send us your resume and tell us how you can contribute.</p>
          <Button onClick={onContact} className="mt-10 bg-cyan-400 hover:bg-cyan-300 text-slate-900 font-semibold h-12 px-8 rounded-md shadow-[0_0_28px_rgba(34,211,238,0.4)]">
            Get in Touch <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </section>

      <Dialog open={applyOpen} onOpenChange={setApplyOpen}>
        <DialogContent className="bg-[#0d1424] border border-cyan-400/20 text-white max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-white">Apply: {selectedJob?.title}</DialogTitle>
            <DialogDescription className="text-slate-400">Submit your application and we'll be in touch.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4 mt-2">
            {/* Honeypot — invisible to humans; bots that auto-fill all fields will trip this */}
            <div aria-hidden="true" style={{ position: 'absolute', left: '-9999px', top: 'auto', width: '1px', height: '1px', overflow: 'hidden' }}>
              <label>Website (leave blank)
                <input
                  type="text"
                  name="website"
                  tabIndex={-1}
                  autoComplete="off"
                  value={form.website}
                  onChange={e => setForm({ ...form, website: e.target.value })}
                />
              </label>
            </div>
            <div>
              <Label className="text-slate-300 text-xs mb-1.5 block">Full Name</Label>
              <Input required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="bg-[#080d18] border-slate-700 focus:border-cyan-400 text-white" />
            </div>
            <div>
              <Label className="text-slate-300 text-xs mb-1.5 block">Email</Label>
              <Input required type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className="bg-[#080d18] border-slate-700 focus:border-cyan-400 text-white" />
            </div>
            <div>
              <Label className="text-slate-300 text-xs mb-1.5 block">Resume (PDF, DOC, DOCX · max 5MB)</Label>
              <label className="flex items-center gap-3 cursor-pointer rounded-md border border-dashed border-slate-700 hover:border-cyan-400 bg-[#080d18] px-4 py-3 transition">
                <Upload className="w-4 h-4 text-cyan-400" />
                <span className="text-sm text-slate-300 truncate flex-1">{resumeFile ? resumeFile.name : 'Click to upload resume'}</span>
                <input type="file" accept=".pdf,.doc,.docx" onChange={onFileChange} className="hidden" />
              </label>
            </div>
            <div>
              <Label className="text-slate-300 text-xs mb-1.5 block">Why IronVision?</Label>
              <Textarea required rows={4} value={form.cover} onChange={e => setForm({ ...form, cover: e.target.value })} className="bg-[#080d18] border-slate-700 focus:border-cyan-400 text-white" />
            </div>
            <Button type="submit" disabled={loading} className="w-full bg-cyan-400 hover:bg-cyan-300 disabled:opacity-70 text-slate-900 font-semibold rounded-md h-11">
              {loading ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting...</>) : 'Submit Application'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Careers;
